import { z } from 'zod';
import { InventoryLabelResolveResponseSchema } from './inventory-label.js';
import { ScannedNodeResolveResponseSchema } from './scanned-node.js';
/** Prefix scanned from session QR to arm a desktop label-link handheld session (not a CNP label). */
export const LABEL_LINK_SESSION_ARM_PREFIX = 'CNAPSE-LINK-ARM:';
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export function buildLabelLinkSessionArmBarcode(sessionId) {
    return `${LABEL_LINK_SESSION_ARM_PREFIX}${sessionId}`;
}
export function parseLabelLinkSessionArmBarcode(raw) {
    const trimmed = raw.trim();
    const upper = trimmed.toUpperCase();
    const prefixUpper = LABEL_LINK_SESSION_ARM_PREFIX.toUpperCase();
    if (!upper.startsWith(prefixUpper)) {
        return null;
    }
    const sessionId = trimmed.slice(LABEL_LINK_SESSION_ARM_PREFIX.length).trim();
    return UUID_REGEX.test(sessionId) ? sessionId : null;
}
export const LabelLinkHandheldSessionStatusSchema = z.enum([
    'pending_arm',
    'armed',
    'label_ready',
    'expired',
    'cancelled',
]);
export const LabelLinkHandheldLastScanBaseSchema = z.object({
    rawBarcode: z.string(),
    scannedAt: z.string().datetime(),
    scannerSerialNumber: z.string(),
});
export const LabelLinkHandheldLabelScanSchema = LabelLinkHandheldLastScanBaseSchema.extend({
    scanKind: z.literal('label'),
    barcode: z.string().nullable(),
    resolve: InventoryLabelResolveResponseSchema,
});
export const LabelLinkHandheldNodeScanSchema = LabelLinkHandheldLastScanBaseSchema.extend({
    scanKind: z.literal('node'),
    nodeId: z.string(),
    resolve: ScannedNodeResolveResponseSchema,
});
/** Backward-compatible: sessions stored before scanKind was added are treated as label scans. */
export const LabelLinkHandheldLastScanSchema = z.preprocess((value) => {
    if (value && typeof value === 'object' && !('scanKind' in value)) {
        return { ...value, scanKind: 'label' };
    }
    return value;
}, z.discriminatedUnion('scanKind', [LabelLinkHandheldLabelScanSchema, LabelLinkHandheldNodeScanSchema]));
export const LabelLinkHandheldSessionSchema = z.object({
    sessionId: z.string().uuid(),
    tenantId: z.string().uuid(),
    userId: z.number().int().positive(),
    /** Set when the handheld scans the session arm QR (deduced from MQTT gateway). */
    gatewaySerialNumber: z.string().min(1).nullable(),
    efemClientId: z.number().int().positive().nullable(),
    /** Scanner that performed the arm scan; used for future per-scanner pairing (edge-guard). */
    armedScannerSerialNumber: z.string().min(1).nullable().optional(),
    status: LabelLinkHandheldSessionStatusSchema,
    armBarcode: z.string(),
    createdAt: z.string().datetime(),
    armedAt: z.string().datetime().nullable().optional(),
    expiresAt: z.string().datetime(),
    lastScan: LabelLinkHandheldLastScanSchema.nullable().optional(),
});
/** No body required — gateway is bound when the handheld scans the session QR. */
export const CreateLabelLinkHandheldSessionRequestSchema = z.object({}).strict();
export const CreateLabelLinkHandheldSessionResponseSchema = z.object({
    success: z.literal(true),
    data: LabelLinkHandheldSessionSchema,
});
export const GetLabelLinkHandheldSessionResponseSchema = z.object({
    success: z.literal(true),
    data: LabelLinkHandheldSessionSchema,
});
export const LabelLinkHandheldAssignRequestSchema = z.object({
    containerId: z.number().int().positive(),
});
export const LabelLinkHandheldAssignResponseSchema = z.object({
    success: z.literal(true),
    data: z.object({
        barcode: z.string(),
        containerId: z.number().int().positive(),
        containerName: z.string().optional(),
    }),
});
export const LabelLinkSessionIdParamsSchema = z.object({
    sessionId: z.string().uuid(),
});
//# sourceMappingURL=label-link-handheld-session.js.map